import { ReactNode } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

type Position = "top-left" | "top-right" | "bottom-left" | "bottom-right";

interface FloatingButtonProps {
  position?: Position;
  href?: string;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
  buttonClassName?: string;
}

const getPositionClasses = (position: Position): string => {
  const positions: Record<Position, string> = {
    "top-left": "top-4 left-2 md:left-4",
    "top-right": "top-4 right-2 md:right-4",
    "bottom-left": "bottom-4 left-2 md:left-4",
    "bottom-right": "bottom-4 right-2 md:right-4",
  };
  return positions[position];
};

export const FloatingButton = ({
  position = "top-left",
  href,
  onClick,
  children,
  className,
  buttonClassName,
}: FloatingButtonProps) => {
  const cardClasses = cn("fixed z-50", getPositionClasses(position), className);

  const cardContent = (
    <Card className="p-1.5 md:p-1.5 shadow-xl border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 rounded-lg md:rounded-md">
      <Button
        variant="ghost"
        size="sm"
        onClick={onClick}
        className={cn(
          "h-7 md:h-6 px-1 py-1 text-xs cursor-pointer transition-all duration-200 rounded-md md:rounded-sm",
          buttonClassName,
        )}
      >
        {children}
      </Button>
    </Card>
  );

  if (href) {
    return (
      <div className={cardClasses}>
        <Link href={href}>{cardContent}</Link>
      </div>
    );
  }

  return <div className={cardClasses}>{cardContent}</div>;
};
